import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * DeleteConfirmDialog Component
 * Confirmation dialog for deleting folders or documents
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback to close the dialog
 * @param {Function} onConfirm - Callback called when delete is confirmed
 * @param {string} itemName - The name of the item to delete
 * @param {string} itemType - The type of item ('folder' or 'document')
 * @param {boolean} loading - Whether the delete operation is in progress
 */
const DeleteConfirmDialog = ({
    open,
    onClose,
    onConfirm,
    itemName,
    itemType = 'item',
    loading = false,
}) => {
    const isFolder = itemType === 'folder';

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                Delete {isFolder ? 'Folder' : 'Document'}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This action cannot be undone.
                    {isFolder && ' Deleting this folder will also delete all its contents.'}
                </Alert>
                <Typography variant="body1">
                    Are you sure you want to delete &quot;{itemName}&quot;?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    color="error"
                    variant="contained"
                    disabled={loading}
                    startIcon={
                        loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
                    }
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {loading ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmDialog;
